import {
  AccountScreen, AppShell, EmptyState, EntryOrNotFound, entryRoutes, Messaging, Notice, NotificationCentre,
  RequireSession, useI18n, usePushRefresh, useSession, useTenantSettings, useUnreadConversations, WatermarkOnRoutes,
  type NavItem,
} from '@daghfal/core';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { Calendar } from './routes/Calendar';
import { ChildInfo } from './routes/ChildInfo';
import { Incident, Incidents } from './routes/Incidents';
import { ChildHome } from './routes/ChildHome';
import { Absence } from './routes/Absence';
import { Complaint } from './routes/Complaint';
import { Complaints } from './routes/Complaints';
import { ChildPicker } from './routes/ChildPicker';
import { ComplaintLink, NotificationsLink, ThreadLink } from './routes/DeepLinks';
import { Day } from './routes/Day';
import { Payments } from './routes/Payments';
import { Photos } from './routes/Photos';
import { GUARDIAN_ROUTES } from './routeMarks';
import { useMyChild } from './myChildren';

export function App() {
  const { t } = useI18n();
  const brand = { name: t('app.name'), tagline: t('app.subtitle') };

  return (
    <Routes>
      {entryRoutes(brand, { slugs: true })}
      <Route path="/*" element={<RequireSession slugEntry><GuardianShell /></RequireSession>} />
    </Routes>
  );
}

function GuardianShell() {
  const { t } = useI18n();
  useTenantSettings();
  usePushRefresh();

  return (
    <Routes>
      {/* The chooser sits outside the child-scoped shell: until a child is selected there is nothing for
          the ☰ menu to point at (FR-SEL-04). */}
      <Route index element={<WatermarkOnRoutes marks={GUARDIAN_ROUTES}><ChildPicker /></WatermarkOnRoutes>} />
      <Route path="child/:childId/*" element={<ChildScopedShell />} />
      {/* A notification's link where it names no child: each finds the child and goes there (Q68). */}
      <Route path="messages/:conversationId" element={<ThreadLink />} />
      <Route path="complaints/:complaintId" element={<ComplaintLink />} />
      <Route path="announcements/*" element={<NotificationsLink />} />
      {/* Outside the child shell: the account is the parent's own, not any one child's. */}
      <Route
        path="account"
        element={
          <AppShell brand={t('app.name')} nav={[]} mobile>
            <AccountScreen />
          </AppShell>
        }
      />
      <Route
        path="*"
        element={
          <AppShell brand={t('app.name')} nav={[]} mobile>
            <EmptyState icon="🔍" title={t('error.notFound')} />
          </AppShell>
        }
      />
    </Routes>
  );
}

/**
 * Everything below a selected child.
 *
 * The child is in the URL rather than in a context value, so that a notification's deep link opens the
 * right child's day directly and a parent with two children can keep two tabs open (FR-SEL-04).
 */
function ChildScopedShell() {
  const { t } = useI18n();
  const { childId } = useParams();
  const { pathname } = useLocation();
  const { session } = useSession();
  const unreadConversations = useUnreadConversations(true);

  // From the chooser's list: whether this child has left with a balance still due.
  const child = useMyChild(Number(childId));

  if (!childId || !Number.isFinite(Number(childId))) return <Navigate to="/" replace />;

  const base = `/child/${childId}`;
  const nursery = session?.membership.tenantNameAr ?? t('app.name');

  // A child who has left keeps the payments screen while a balance is due, and nothing else (Q32).
  if (child?.paymentsOnly) {
    if (pathname !== `${base}/payments`) return <Navigate to={`${base}/payments`} replace />;

    return (
      <AppShell
        brand={nursery}
        nav={[{ to: `${base}/payments`, label: t('nav.payments'), icon: '💳' }]}
        mobile
        watermarkRoutes={GUARDIAN_ROUTES}
      >
        <Notice tone="info">{t('payments.afterLeaving')}</Notice>
        <Payments childId={Number(childId)} />
      </AppShell>
    );
  }

  const nav: NavItem[] = [
    { to: base, label: t('nav.home'), icon: '🏠', end: true },
    { to: `${base}/day`, label: t('nav.day'), icon: '📔' },
    { to: `${base}/photos`, label: t('nav.photos'), icon: '📸' },
    { to: `${base}/messages`, label: t('nav.messages'), icon: '💬', count: unreadConversations },
    // Every guardian of the child sees the account (Q14, Q42).
    { to: `${base}/payments`, label: t('nav.payments'), icon: '💳' },
    { to: `${base}/attendance`, label: t('nav.attendance'), icon: '🗓️' },
    { to: `${base}/complaints`, label: t('nav.complaints'), icon: '🗒️' },
    { to: `${base}/info`, label: t('nav.info'), icon: 'ℹ️' },
  ];

  return (
    <AppShell
      brand={nursery} nav={nav} mobile notificationsPath={`${base}/notifications`} watermarkRoutes={GUARDIAN_ROUTES}
    >
      <Routes>
        <Route index element={<ChildHome childId={Number(childId)} />} />
        <Route path="day" element={<Day childId={Number(childId)} />} />
        <Route path="photos" element={<Photos childId={Number(childId)} />} />
        <Route path="messages" element={<Messaging childContextId={Number(childId)} />} />
        {/* No permission guard: a guardian's access to money follows their link on this child, which the
            API checks, not a permission an administrator could grant to the wrong parent. */}
        <Route path="payments" element={<Payments childId={Number(childId)} />} />
        <Route path="attendance" element={<Absence childId={Number(childId)} />} />
        {/* Complaints sit under the child shell because most concern a child, though the child is
            optional on the record itself. The deep link a notification carries points here. */}
        <Route path="complaints" element={<Complaints childId={Number(childId)} />} />
        <Route path="complaints/:complaintId" element={<Complaint childId={Number(childId)} />} />
        <Route path="notifications" element={<NotificationCentre />} />
        {/* An incident's notification opens its record, where the guardian acknowledges it (FR-CHD-12). */}
        <Route path="incidents" element={<Incidents childId={Number(childId)} />} />
        <Route path="incidents/:incidentId" element={<Incident childId={Number(childId)} />} />
        {/* A dose's notification links to the child's health, which has no screen of its own yet. */}
        <Route path="health" element={<Navigate to={`${base}/notifications`} replace />} />
        <Route path="info" element={<ChildInfo childId={Number(childId)} />} />
        <Route path="calendar" element={<Calendar childId={Number(childId)} />} />
        <Route path="*" element={<EntryOrNotFound><EmptyState icon="🔍" title={t('error.notFound')} /></EntryOrNotFound>} />
      </Routes>
    </AppShell>
  );
}
